import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IOnGetSerialsDTO from "../models/DTO/IOnGetSerialsDTO";
import IGetSerialsQuery from "@models/DTO/IGetSerialsQuery";
import IBulkAssignSerialsDTO from "../models/DTO/IBulkAssignSerialsDTO";
import IBulkCreateSerialsDTO from "../models/DTO/IBulkCreateSerialsDTO";
import IOnBulkCreateSerialsDTO, { IBulkCreateSerialError } from "../models/DTO/IOnBulkCreateSerialsDTO";
import IBulkDeleteSerialsDTO from "../models/DTO/IBulkDeleteSerialsDTO";
import IOnBulkDeleteSerialsDTO from "../models/DTO/IOnBulkDeleteSerialsDTO";
import IProcessSerialDTO from "../models/DTO/IProcessSerialDTO";
import IOnBulkAssignSerialsDTO from "../models/DTO/IOnBulkAssignSerialsDTO";
import IReclassifySerialDTO from "../models/DTO/IReclassifySerialDTO";
import IOnReclassifySerialDTO from "../models/DTO/IOnReclassifySerialDTO";

const getSerials = async (
    query: IGetSerialsQuery,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnGetSerialsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsGetAll,
        query,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch serials: ${response.statusText}`);
    }

    return (await response.json()) as IOnGetSerialsDTO;
};

/**
 * Assign serials to one order line. Resolves with the per-serial outcome: a serial that fails
 * (wrong SKU, already used, over the line's quantity) is listed in `errors`, it does not reject.
 */
const bulkAssignSerials = async (
    dto: IBulkAssignSerialsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnBulkAssignSerialsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsBulkAssign,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(dto),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to bulk assign serials: ${response.statusText}`);
    }

    // The API always answers { assigned, errors }. Anything else is a broken response, not "nothing
    // assigned": reject it rather than report a silent no-op.
    const result: unknown = await response.json().catch(() => null);
    if (!isBulkAssignOutcome(result)) {
        throw new Error("Invalid bulk assign response");
    }

    return result;
};

const isBulkAssignOutcome = (value: unknown): value is IOnBulkAssignSerialsDTO => {
    const outcome = value as IOnBulkAssignSerialsDTO | null;
    return (
        typeof outcome === "object" &&
        outcome !== null &&
        typeof outcome.assigned === "number" &&
        Array.isArray(outcome.errors) &&
        outcome.errors.every((error) => typeof error?.serialId === "number" && typeof error?.reason === "string")
    );
};

/**
 * Set a serial's finished product (re-SKU, OR-1459). The serial number never changes; only its
 * catalog item does. The API moves the serial's inventory contribution and BOM consumption across.
 */
const reclassifySerial = async (
    serialId: number,
    dto: IReclassifySerialDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnReclassifySerialDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsReclassify,
        params: { serialId: String(serialId) },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(dto),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to reclassify serial: ${response.statusText}`);
    }

    return (await response.json()) as IOnReclassifySerialDTO;
};

const deleteSerial = async (
    serialId: number,
    serialValue: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<void> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsProcessSerial,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const body: Partial<IProcessSerialDTO> = {
        serial: serialValue,
        serialId,
        deleteFlag: true,
    };

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to delete serial: ${response.statusText}`);
    }
};

/** Serials per bulk-create request: keeps each request short and lets the UI show real progress. */
export const BULK_CREATE_CHUNK_SIZE = 50;

/** Most serials the API deletes per bulk-delete request. */
export const BULK_DELETE_CHUNK_SIZE = 500;

const chunk = <T>(items: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

/**
 * Create serials for one catalog item (optionally assigned to an order line), in requests of
 * BULK_CREATE_CHUNK_SIZE. Resolves with the combined outcome: values the API skipped (already exists,
 * duplicate in batch, format) are listed in `errorItems`, they do not reject. `onProgress` is called after
 * each request with how many values have been processed.
 *
 * Rejects when a request fails outright; the message says how many values were processed before it, and
 * re-submitting is safe because existing values are reported, never duplicated (OR-1550).
 */
const bulkCreateSerials = async (
    dto: IBulkCreateSerialsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    onProgress?: (processed: number, total: number) => void
): Promise<IOnBulkCreateSerialsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsBulkCreate,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const total = dto.serials.length;
    const combined: IOnBulkCreateSerialsDTO = { serials: [], errors: [], errorItems: [] };
    let processed = 0;

    for (const serials of chunk(dto.serials, BULK_CREATE_CHUNK_SIZE)) {
        const response = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify({ ...dto, serials }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            const reason = errorData?.message || `Failed to bulk create serials: ${response.statusText}`;
            throw new Error(processed > 0 ? `${reason} (stopped after ${processed} of ${total} serials)` : reason);
        }

        // A body that is not a bulk-create outcome is a broken response, not "everything created".
        const result: unknown = await response.json().catch(() => null);
        if (!isBulkCreateOutcome(result)) {
            const reason = "Invalid bulk create response";
            throw new Error(processed > 0 ? `${reason} (stopped after ${processed} of ${total} serials)` : reason);
        }

        combined.serials.push(...result.serials);
        combined.errors.push(...result.errors);
        // An API from before OR-1550 answers without errorItems: show its "<value>: <reason>" strings instead.
        combined.errorItems.push(...(result.errorItems ?? result.errors.map((reason) => ({ serial: "", reason }))));

        processed += serials.length;
        onProgress?.(processed, total);
    }

    return combined;
};

/** One bulk-create answer. errorItems is optional: an API from before OR-1550 sends only the errors strings. */
type BulkCreateResponse = Omit<IOnBulkCreateSerialsDTO, "errorItems"> & { errorItems?: IBulkCreateSerialError[] };

const isBulkCreateOutcome = (value: unknown): value is BulkCreateResponse => {
    const outcome = value as Partial<IOnBulkCreateSerialsDTO> | null;
    return (
        typeof outcome === "object" &&
        outcome !== null &&
        Array.isArray(outcome.serials) &&
        Array.isArray(outcome.errors) &&
        outcome.errors.every((error) => typeof error === "string") &&
        (outcome.errorItems === undefined ||
            (Array.isArray(outcome.errorItems) &&
                outcome.errorItems.every((item) => typeof item?.serial === "string" && typeof item?.reason === "string")))
    );
};

/**
 * Delete serials from the Serials screen (OR-1550), in requests of BULK_DELETE_CHUNK_SIZE. Only serials that
 * are not assigned to an order and not released are deleted; the API reverses inventory counters and BOM
 * reservations per serial. Resolves with the combined outcome: a serial that could not be deleted is listed
 * in `errors`, it does not reject.
 *
 * Rejects only when the first request fails (nothing was deleted). When a later request fails, it resolves with
 * what the earlier requests deleted and lists every serial of the failed and unsent requests in `errors` with
 * the failure, so the caller can drop the deleted ones and keep the rest for a retry.
 */
const bulkDeleteSerials = async (
    serialIds: number[],
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnBulkDeleteSerialsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsBulkDelete,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const combined: IOnBulkDeleteSerialsDTO = { deleted: 0, errors: [] };
    const chunks = chunk(serialIds, BULK_DELETE_CHUNK_SIZE);

    for (let index = 0; index < chunks.length; index++) {
        const dto: IBulkDeleteSerialsDTO = { serialIds: chunks[index] };
        let failure: string | null = null;
        let result: unknown = null;

        try {
            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(dto),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                failure = errorData?.message || `Failed to delete serials: ${response.statusText}`;
            } else {
                result = await response.json().catch(() => null);
                if (!isBulkDeleteOutcome(result)) {
                    failure = "Invalid bulk delete response";
                }
            }
        } catch (err) {
            // Network failure. The request may still have reached the API; the list refresh shows what was deleted.
            failure = (err as Error)?.message || "Failed to delete serials";
        }

        if (failure !== null) {
            if (index === 0) {
                throw new Error(failure);
            }

            // Earlier requests deleted serials: report them, and leave this request's and the unsent ones for a retry.
            const reason = failure;
            combined.errors.push(...chunks.slice(index).flatMap((ids) => ids.map((serialId) => ({ serialId, reason }))));
            return combined;
        }

        const outcome = result as IOnBulkDeleteSerialsDTO;
        combined.deleted += outcome.deleted;
        combined.errors.push(...outcome.errors);
    }

    return combined;
};

const isBulkDeleteOutcome = (value: unknown): value is IOnBulkDeleteSerialsDTO => {
    const outcome = value as IOnBulkDeleteSerialsDTO | null;
    return (
        typeof outcome === "object" &&
        outcome !== null &&
        typeof outcome.deleted === "number" &&
        Array.isArray(outcome.errors) &&
        outcome.errors.every((error) => typeof error?.serialId === "number" && typeof error?.reason === "string")
    );
};

const SerialService = {
    getSerials,
    bulkAssignSerials,
    bulkCreateSerials,
    bulkDeleteSerials,
    deleteSerial,
    reclassifySerial,
};

export default SerialService;
